/**
 * The signed-in person's own queue on this desk (087): saved views, the
 * default, row colors. Read once per desk and written whole.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EMPTY_QUEUE_PREFS, type QueuePrefs } from '@esa/shared';
import { ApiError, api } from './api.js';
import { useToast } from '../ui/index.js';

export function useQueuePrefs(tenant: string, enabled: boolean) {
  const qc = useQueryClient();
  const toast = useToast();
  const key = ['queue-prefs', tenant];
  const q = useQuery({
    queryKey: key,
    queryFn: () => api.get<QueuePrefs>(`/api/v1/tenants/${tenant}/me/queue-prefs`),
    enabled: enabled && Boolean(tenant),
    staleTime: 5 * 60_000,
  });
  const save = useMutation({
    mutationFn: (next: QueuePrefs) => api.put<QueuePrefs>(`/api/v1/tenants/${tenant}/me/queue-prefs`, next),
    // Shown straight away; the server's copy replaces it when it answers.
    onMutate: (next) => qc.setQueryData(key, next),
    onSuccess: (saved) => qc.setQueryData(key, saved),
    // Said out loud: a view or color that silently does not stick looks like
    // a feature that does not work.
    onError: (e) => {
      void qc.invalidateQueries({ queryKey: key });
      toast.error('Not saved', e instanceof ApiError ? e.message : e instanceof Error ? e.message : undefined);
    },
  });
  return { prefs: q.data ?? EMPTY_QUEUE_PREFS, loaded: q.isSuccess, save };
}
